import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useRole } from "@/contexts/AuthContext";
import { track, EVENTS } from "@/lib/analytics";
import { AlertOctagon, Loader2, ShieldCheck, Play, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";

const STAGES = {
  idle: { label: "Standby", color: "#94A3B8" },
  alert: { label: "Critical Alert", color: "#EF4444" },
  routing: { label: "Triggering Security Playbook...", color: "#F59E0B" },
  success: { label: "Attack Mitigated", color: "#10B981" },
};

export default function BruteForceSimulator({ onMitigated }) {
  const { tenant } = useTenant();
  const { canWrite } = useRole();
  const [stage, setStage] = useState("idle");
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  const run = async () => {
    if (stage === "routing") return;
    setStage("alert");
    setDetail(null);
    track(EVENTS.SIMULATE_SOC_ATTACK, { type: "brute_force", target: "HQ" });
    try {
      const { data } = await api.post(`/simulate/brute-force?tenant=${tenant.id}`);
      setDetail(data);
      await new Promise((r) => setTimeout(r, 1100));
      setStage("routing");
      const mitigatePromise = api.post(`/soc/mitigate`, {
        alert_id: data.alert_id,
        tenant: tenant.id,
        playbook: data.recommended_playbook,
      });
      await new Promise((r) => setTimeout(r, 3000));
      await mitigatePromise;
      setStage("success");
      toast.success("Attack mitigated", {
        description: `IP ${data.source_ip} blocked at the edge across all federated firewalls`,
        action: { label: "History", onClick: () => navigate("/agents?tab=playbooks") },
      });
      setTimeout(() => {
        toast("Agente Sentinel investigando…", {
          description: "Investigação autônoma em curso. Acompanhe no Terminal Agêntico.",
          action: { label: "Abrir Agentes", onClick: () => navigate(`/agents?investigate=${encodeURIComponent(data.source_ip)}`) },
        });
      }, 600);
      onMitigated?.();
    } catch (e) {
      setStage("idle");
      toast.error("Simulation failed");
    }
  };

  const reset = () => {
    setStage("idle");
    setDetail(null);
  };

  const s = STAGES[stage];

  return (
    <div className="p-5 space-y-4" data-testid="brute-force-simulator">
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] font-mono font-bold px-2.5 py-1 border rounded-sm uppercase tracking-wider"
          style={{ color: s.color, borderColor: s.color + "55", background: s.color + "12" }}
          data-testid="bf-stage"
        >
          {stage !== "idle" && <span className="evos-blink mr-1">●</span>}
          {s.label}
        </span>
      </div>

      <p className="text-xs text-slate-400 leading-relaxed">
        Simulates a credential-stuffing brute-force attack from a public IP targeting the HQ firewall cluster.
        On execution, the Block Malicious IP at Edge playbook fires across all federated firewalls.
      </p>

      {stage === "idle" && (
        <button
          data-testid="simulate-bf-btn"
          onClick={run}
          disabled={!canWrite}
          title={!canWrite ? "Read-only access" : ""}
          className="w-full bg-red-500 hover:bg-red-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-bold px-4 py-3 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors uppercase tracking-wider"
        >
          {canWrite ? <Play className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          Simulate Brute Force Attack on HQ
        </button>
      )}

      {stage !== "idle" && detail && (
        <div className="space-y-3" data-testid="bf-flow">
          {/* Critical alert */}
          <div className="border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-3">
            <AlertOctagon className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0 evos-blink" />
            <div className="flex-1 text-xs">
              <div className="font-semibold text-red-200 mb-1">Brute Force Attack Detected</div>
              <div className="text-red-300/80 text-[11px] leading-relaxed">
                From IP <span className="font-mono">{detail.source_ip}</span> on <span className="font-mono">{detail.target}</span>
              </div>
            </div>
          </div>

          {/* Routing loader */}
          {stage === "routing" && (
            <div className="flex items-center gap-2 px-3 py-2 border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs" data-testid="bf-routing">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="font-mono uppercase tracking-wider">Triggering Security Playbook...</span>
            </div>
          )}

          {/* Success */}
          {stage === "success" && (
            <>
              <div className="border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-start gap-3" data-testid="bf-success">
                <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 text-xs">
                  <div className="font-semibold text-emerald-200 mb-1">Playbook Executed</div>
                  <div className="text-emerald-300/80 text-[11px] leading-relaxed">
                    IP <span className="font-mono">{detail.source_ip}</span> blocked at the edge across all federated firewalls. Attack mitigated.
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  data-testid="bf-reset"
                  onClick={reset}
                  className="flex-1 border border-slate-800 hover:border-slate-700 text-slate-300 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-sm transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={() => navigate("/agents?tab=playbooks")}
                  className="flex items-center gap-1 bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white text-xs font-semibold px-3 py-2 rounded-sm transition-colors"
                >
                  View Automation History <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
